// Plain-text previews for the Emails inbox. Emails composed in the app store only
// htmlBody (textBody is empty), and inbound HTML-only messages have no text part,
// so the list falls back to a text rendering of the HTML at display time.

export const EMPTY_PREVIEW = '(no content)'

// Tags are stripped from at most this much markup so a huge newsletter does not
// slow the list down; previews only ever need the start of the message.
const MAX_HTML_SCANNED = 30000

const NAMED_ENTITIES: Record<string, string> = {
  nbsp: ' ',
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  ndash: '–',
  mdash: '—',
  hellip: '…',
  lsquo: '‘',
  rsquo: '’',
  ldquo: '“',
  rdquo: '”',
  bull: '•',
  middot: '·',
  copy: '©',
  reg: '®',
  trade: '™',
}

// Elements that separate text visually; replaced with a space so words from
// neighbouring cells or paragraphs do not run together.
const BREAKING_TAG = /<\/?(?:br|p|div|tr|td|th|li|ul|ol|table|thead|tbody|h[1-6]|blockquote|hr|section|article|header|footer)\b[^>]*>/gi
const ANY_TAG = /<\/?[a-z][^\s>/]*(?:"[^"]*"|'[^']*'|[^>"'])*>/gi

function fromCodePoint(code: number): string {
  if (!Number.isFinite(code) || code <= 0 || code > 0x10ffff) return ''
  try {
    return String.fromCodePoint(code)
  } catch {
    return ''
  }
}

function decodeEntities(text: string): string {
  return text.replace(/&(#x[0-9a-f]+|#\d+|[a-z][a-z0-9]*);/gi, (match, entity: string) => {
    if (entity[0] === '#') {
      const isHex = entity[1] === 'x' || entity[1] === 'X'
      return fromCodePoint(parseInt(entity.slice(isHex ? 2 : 1), isHex ? 16 : 10)).replace(' ', ' ')
    }
    return NAMED_ENTITIES[entity.toLowerCase()] ?? match
  })
}

function collapseWhitespace(text: string): string {
  return text.replace(/[\s ​-‍﻿͏]+/g, ' ').trim()
}

export function htmlToPlainText(html: string): string {
  const withoutHidden = html
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/<!doctype[^>]*>/gi, ' ')
    .replace(/<(style|script|head|title)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, ' ')
    .slice(0, MAX_HTML_SCANNED)
  const stripped = withoutHidden.replace(BREAKING_TAG, ' ').replace(ANY_TAG, '')
  return collapseWhitespace(decodeEntities(stripped))
}

interface PreviewSource {
  textBody?: string | null
  htmlBody?: string | null
}

// Some automated emails (the welcome email) were saved with their HTML in
// textBody. Only well-known HTML tags count, so a plain-text body that contains
// something like <john@example.com> is left alone.
const HTML_MARKUP =
  /<(?:!doctype|\/?(?:html|head|body|style|p|div|span|table|tbody|thead|tr|td|th|br|hr|a|b|i|u|em|strong|ul|ol|li|h[1-6]|img|font|center|blockquote|section|article)\b)/i

// Returns up to maxLength characters of the message as plain text, or
// "(no content)" when neither part has any visible text.
export function emailPreview(email: PreviewSource, maxLength: number): string {
  const textBody = email.textBody ?? ''
  const fromText = HTML_MARKUP.test(textBody) ? htmlToPlainText(textBody) : collapseWhitespace(textBody)
  const text = fromText || (email.htmlBody ? htmlToPlainText(email.htmlBody) : '')
  return text ? text.slice(0, maxLength) : EMPTY_PREVIEW
}
